/**
 * @file IntakeInboxPanel.tsx
 * @brief Incoming clinic assessments pushed by partner software (MEDYX/Equire).
 *
 * @author DES
 * @date 02-09-2026
 */

import { SkeletonRows } from "@/components/ui/Skeleton";
import { Inbox, LoaderCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { ApiClientError } from "@/shared/api/client";
import { toast } from "@/shared/hooks/useToast";
import { formatDateTime } from "@/shared/utils/formatters";
import { GridBlock, GridCell, GridCells } from "@/features/analytics/components/GridBlock";
import {
  useAcceptMedicalIntake,
  useMedicalIntakeInbox,
} from "../api";
import {
  assessmentClaimantName,
  assessmentReference,
  intakeSourceLabel,
} from "../assessmentListDisplay";
import type { MedicalAssessment } from "../schemas";

function scheduleLine(item: MedicalAssessment): string | undefined {
  const s = item.caseHub?.schedule;
  if (!s) return undefined;
  return [s.appointmentDate, s.appointmentTime, s.clinicName]
    .filter(Boolean)
    .join(" · ");
}

export function IntakeInboxPanel() {
  const navigate = useNavigate();
  const inbox = useMedicalIntakeInbox();
  const acceptIntake = useAcceptMedicalIntake();
  const items = inbox.data ?? [];

  const onOpen = async (item: MedicalAssessment) => {
    try {
      const assessment =
        item.status === "intake_pending"
          ? await acceptIntake.mutateAsync(item.id)
          : item;
      navigate(`/company/medical-assessment/${assessment.id}`);
    } catch (err) {
      const message =
        err instanceof ApiClientError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Could not open assessment";
      toast({ title: "Open failed", description: message });
    }
  };

  return (
    <GridBlock>
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border/60 px-4 py-3">
        <div className="flex items-center gap-2">
          <Inbox className="h-4 w-4 text-muted-foreground" />
          <div>
            <h3 className="text-sm font-semibold">Incoming assessments</h3>
            <p className="text-xs text-muted-foreground">
              Pushed from clinic software — routed to your doctor account by email.
            </p>
          </div>
        </div>
        {inbox.isFetching ? (
          <LoaderCircle className="h-4 w-4 animate-spin text-muted-foreground" />
        ) : (
          <Badge variant="secondary">{items.length} pending</Badge>
        )}
      </div>

      {inbox.isLoading ? (
        <SkeletonRows rows={3} twoLine />
      ) : items.length === 0 ? (
        <p className="px-4 py-6 text-sm text-muted-foreground">
          No incoming assessments. Your clinic software will push cases here when
          configured with the VERBLIZR integration key.
        </p>
      ) : (
        <GridCells className="gap-0 divide-y divide-border/60">
          {items.map((item) => {
            const ref = assessmentReference(item);
            const schedule = scheduleLine(item);
            const source = intakeSourceLabel(item);
            return (
              <GridCell key={item.id} className="flex flex-wrap items-start justify-between gap-3 py-4">
                <div className="min-w-0 space-y-1">
                  <p className="font-medium">{assessmentClaimantName(item)}</p>
                  {ref ? (
                    <p className="text-xs text-muted-foreground">Ref {ref}</p>
                  ) : null}
                  {schedule ? (
                    <p className="text-xs text-muted-foreground">{schedule}</p>
                  ) : null}
                  <p className="text-xs text-muted-foreground">
                    {source} · received {formatDateTime(item.createdAt)}
                  </p>
                </div>
                <Button
                  size="sm"
                  onClick={() => void onOpen(item)}
                  disabled={acceptIntake.isPending}
                >
                  {acceptIntake.isPending ? (
                    <LoaderCircle className="mr-2 h-4 w-4 animate-spin" />
                  ) : null}
                  Open &amp; record
                </Button>
              </GridCell>
            );
          })}
        </GridCells>
      )}
    </GridBlock>
  );
}
